import lang from "translations";
import {
  formatName,
  prepareChart,
  denormalizePlotDateTimeFromApiResponse,
  getApiDateTimeFormatByTimeUnit,
  getDateTimeFormatByTimeUnit,
  formatNumberWithComma,
  formatNumberToOrdinal,
  timeAgo,
  prettifyTransactionType,
  prettifyPaymentMethod,
  isFailedTransaction,
} from "services";
import { formatNumberToMoneyWithCurrencySymbol } from "services/money.service";
import * as moment from "moment";
import {
  dateTimeRequest,
  dateToTimeAgo,
  filterRequest,
  selectToObjectRequest,
} from "./common.mapper";
import {
  DateTime,
  ExternalPaymentStatus,
  ExternalPaymentVoidStatus,
  PaymentMethod,
  TimeUnit,
  TransactionStatus,
  TransactionType,
} from "enums";
import { add, subtract, multiply, divide, toAmount } from "services/number.service";
import { getCurrentTimeWithTimezone, buildItemLookup, mapSplitItems } from "./common.mapper";
import { Text } from "components";
import { calcQuarterDataGraph, calcWeekDataGraph } from "services/chart.service";

export const transactionListResponse = {
  transactionId: { key: "transactionId" },
  dateCreated: dateToTimeAgo("deviceUpdatedDate"),
  guestId: { key: "guestId" },
  guestName: {
    transform: ({ src }) => {
      var name = formatName(src.userFirstName, src.userLastName);
      if (!name && src.guestId) {
        name = `Guest ${src.guestId ? src.guestId : ""}`.trim();
      }
      return name;
    },
  },
  userTagUid: { key: "userTagUid" },
  userTagUseCount: {
    transform: ({ src }) => {
      return lang.populate(lang.nthUse, [formatNumberToOrdinal(src.userTagUseCount)]);
    },
  },
  locationName: { key: "locationName" },
  locationId: { key: "locationId" },
  staffName: {
    transform: ({ src }) => {
      return formatName(src.staffFirstName, src.staffLastName);
    },
  },
  staffTagUid: { key: "staffTagUid" },
  staffProfileId: { key: "staffProfileId" },
  transactionStatus: { key: "transactionStatus" },
  transactionType: { key: "type" },
  tapStatus: { key: "nfcStatus" },
  paidAmount: {
    transform: ({ self }) => {
      return formatNumberToMoneyWithCurrencySymbol(self);
    },
  },
  items: {
    transform: ({ src }) => {
      if (src.paymentMethod && src.type === TransactionType.TOPUP) {
        return [
          {
            name: prettifyPaymentMethod(src.paymentMethod, src.dynamicPaymentMethod),
            deleted: false,
          },
        ];
      }
      if (src?.transactionItems) {
        return src.transactionItems.map((item) => {
          const variants = item.productVariants?.length
            ? item.productVariants?.map((variant) => {
              return variant.value;
            })
            : [];

          if (variants?.length) {
            return {
              name: `${item.productName} · ${variants.join(" · ")}`,
              deleted: item.productSkuDeleted,
            };
          }

          return { name: item.productName, deleted: item.productSkuDeleted };
        });
      }
      return [];
    },
  },
  qty: {
    transform: ({ src }) => {
      let qty = 0;
      if (src?.transactionItems.length) {
        src.transactionItems.forEach((item) => {
          qty = qty + item.quantity;
        });
        return toAmount(qty, "0,0");
      }

      return "";
    },
  },
  paymentMethod: {
    key: "paymentMethod",
  },
  isCheckout: {
    key: "isCheckout",
  },
  externalPaymentStatus: {
    key: "externalPaymentStatus",
  },
  isVoided: {
    transform: ({ src }) => {
      if (src.externalPaymentVoidStatus) {
        return true;
      }
      return Boolean(
        src.transactionStatus === TransactionStatus.VOID || src.voidAuthorizationProfileId
      );
    },
  },
  isFailed: {
    transform: ({ src }) => {
      return isFailedTransaction(
        src.externalPaymentVoidStatus ? src.externalPaymentVoidStatus : src.externalPaymentStatus,
        src.nfcStatus
      );
    },
  },
};

export const transactionListFilterRequest = {
  ...filterRequest,
  ...dateTimeRequest,
  orders: {
    transform: ({ src }) => {
      return { [`t.deviceCreatedDate`]: src.sort.value };
    },
  },
  locationIds: selectToObjectRequest("locations"),
  staffIds: selectToObjectRequest("staffs"),
  shiftIds: {
    transform: ({ src }) => {
      if (src.shifts.length > 0) {
        return src.shifts.split(",").map((s) => parseInt(s));
      }
      return [];
    },
  },
  transactionStatus: { key: "transactionStatus" },
  transactionTypes: {
    transform: ({ self }) => {
      if (!self || self.length === 0 || self[0] === undefined) {
        return [
          TransactionType.SALE,
          TransactionType.TOPUP,
          TransactionType.RETURN,
          TransactionType.REDEEM,
          TransactionType.MIXED,
          TransactionType.ISSUE_FOC,
          TransactionType.REMOVE_FOC,
          TransactionType.VOUCHER_ISSUE,
          TransactionType.VOUCHER_REMOVE,
        ];
      } else {
        return self.map((transactionType) => {
          return transactionType.value;
        });
      }
    },
  },
  nfcStatus: { key: "tapStatus" },
  externalPaymentStatus: {
    transform: ({ src }) => {
      if (!src.externalPaymentStatus || !src.externalPaymentStatus.length) return [];
      return src.externalPaymentStatus.map(({ value }) => value);
    },
  },
};

export const graphLineChartOfTotalSale = {
  data: {
    transform: (arg) => {
      const { src, params } = arg;
      const plot = denormalizePlotDateTimeFromApiResponse(src.plot, params.interval);
      return prepareChart(plot);
    },
  },
  total: {
    transform: ({ src }) => {
      return formatNumberToMoneyWithCurrencySymbol(src.totalSales);
    },
  },
  _keys: ["totalSales"],
};

export const graphMultiLineChartOfTotalSale = {
  data: {
    transform: ({ src, params }) => {
      let labels = [];
      const data = src.locations.map((location) => {
        const plot = prepareChart(
          denormalizePlotDateTimeFromApiResponse(location.plot, params.interval)
        );
        labels = plot.labels;
        return {
          label: location.location.locationName,
          ...plot,
        };
      });
      return {
        data,
        labels,
      };
    },
  },
  total: {
    transform: ({ src }) => {
      let total = 0;
      src.locations.forEach((location) => {
        total = location.totalSales + total;
      });
      return formatNumberToMoneyWithCurrencySymbol(total);
    },
  },
  totalSale: {
    transform: ({ src }) => {
      let total = 0;
      src.locations.forEach((location) => {
        total = location.totalSales + total;
      });
      return total;
    },
  },
};

export const salesReportListRequest = {
  ...filterRequest,
  ...dateTimeRequest,
  totalSales: { key: "totalSales" },
  requestedAt: getCurrentTimeWithTimezone(),
};

export const graphPieChartOfTopLocationSales = {
  name: {
    transform: ({ src }) => {
      return src.locationName;
    },
  },
  value: {
    transform: ({ src }) => {
      return src.totalSale;
    },
  },
  _keys: ["totalSale"],
};

const barSetting = {
  CASH: {
    label: lang.cash,
    color: "#FDC02F",
  },
  CREDIT: {
    label: lang.card,
    color: "#FED45B",
  },
  CREDIT_DEBIT: {
    label: lang.card,
    color: "#FED45B",
  },
  OTHERS: {
    label: lang.others,
    color: "#FAE3A1",
  },
};

const barSettingTotalSales = {
  SALES_CREDITS: {
    label: lang.credits,
    color: "#FDC02F",
  },
  SALES_FREE_CREDITS: {
    label: lang.freeCredits,
    color: "#FED45B",
  },
  SALES_CASH: {
    label: lang.cash,
    color: "#2A8BE6",
  },
  SALES_CREDIT_DEBIT: {
    label: lang.creditDebit,
    color: "#4CA7FC",
  },
  E_WALLET: {
    label: lang.eWallet,
    color: "#0F0",
  },
  SALES_OTHERS: {
    label: lang.others,
    color: "#DEEEFC",
  },
};

export const graphTotalSales = {
  sales: { key: "sales" },
  salesCash: { key: "salesCash" },
  salesCreditDebit: { key: "salesCreditDebit" },
  salesCredits: { key: "salesCredits" },
  salesFreeCredits: { key: "salesFreeCredits" },
  salesOthers: { key: "salesOthers" },
  grossProfit: { key: "grossProfit" },
  supplyPrice: { key: "supplyPrice" },
  taxes: { key: "taxes" },
  discounts: { key: "discounts" },
  voidedItems: { key: "voidedItems" },
  voidedValue: { key: "voidedValue" },
  itemsSold: { key: "itemsSold" },
  barChartData: {
    transform: ({ src }) => {
      let credits = [
        {
          label: barSettingTotalSales.SALES_CREDITS.label,
          color: barSettingTotalSales.SALES_CREDITS.color,
          value: src.salesCredits,
        },
        {
          label: barSettingTotalSales.SALES_FREE_CREDITS.label,
          color: barSettingTotalSales.SALES_FREE_CREDITS.color,
          value: src.salesFreeCredits,
        },
        {
          label: barSettingTotalSales.SALES_CASH.label,
          color: barSettingTotalSales.SALES_CASH.color,
          value: src.salesCash,
        },
        {
          label: barSettingTotalSales.SALES_CREDIT_DEBIT.label,
          color: barSettingTotalSales.SALES_CREDIT_DEBIT.color,
          value: src.salesCreditDebit,
        },
        {
          label: barSettingTotalSales.E_WALLET.label,
          color: barSettingTotalSales.E_WALLET.color,
          value: src.salesEWallet,
        },
        {
          label: barSettingTotalSales.SALES_OTHERS.label,
          color: barSettingTotalSales.SALES_OTHERS.color,
          value: src.salesOthers,
        },
      ];
      return credits;
    },
  },
};

export const barStackChartTotalSales = {
  data: {
    transform: ({ src, params }) => {
      let creditSalesPlot = [];
      let freeCreditSalesPlot = [];
      let cashSalesPlot = [];
      let creditDebitSalesPlot = [];
      let ewalletSalesPlot = [];
      let otherSales = [];

      src.plot.forEach((plot) => {
        const keys = Object.keys(plot);
        keys.forEach((key, index) => {
          creditSalesPlot.push({
            [key]: plot[key].creditSales,
          });
          freeCreditSalesPlot.push({
            [key]: plot[key].freeCreditSales,
          });
          cashSalesPlot.push({
            [key]: plot[key].cashSales,
          });
          creditDebitSalesPlot.push({
            [key]: plot[key].creditDebitSales,
          });
          ewalletSalesPlot.push({ [key]: plot[key].ewalletSales });
          otherSales.push({
            [key]: plot[key].otherSales,
          });
        });
      });

      const chartForCreditSales = prepareChart(
        denormalizePlotDateTimeFromApiResponse(creditSalesPlot, params.splitType)
      );
      const chartForFreeCreditSales = prepareChart(
        denormalizePlotDateTimeFromApiResponse(freeCreditSalesPlot, params.splitType)
      );
      const chartForCashSales = prepareChart(
        denormalizePlotDateTimeFromApiResponse(cashSalesPlot, params.splitType)
      );
      const chartForCreditDebitSales = prepareChart(
        denormalizePlotDateTimeFromApiResponse(creditDebitSalesPlot, params.splitType)
      );
      const chartForEWalletSales = prepareChart(
        denormalizePlotDateTimeFromApiResponse(ewalletSalesPlot, params.splitType)
      );
      const chartForOtherSales = prepareChart(
        denormalizePlotDateTimeFromApiResponse(otherSales, params.splitType)
      );
      let labels = chartForCreditSales.labels;
      return {
        data: [
          { ...chartForCreditSales, label: lang.credits },
          { ...chartForFreeCreditSales, label: lang.freeCredits },
          { ...chartForCashSales, label: lang.cashSales },
          { ...chartForCreditDebitSales, label: lang.creditDebitSales },
          { ...chartForEWalletSales, label: lang.eWallet },
          { ...chartForOtherSales, label: lang.others },
        ],
        labels,
      };
    },
  },
  creditSales: {
    transform: ({ src }) => {
      return {
        text: formatNumberToMoneyWithCurrencySymbol(src.creditSales),
        value: src.creditSales,
      };
    },
  },
  freeCreditSales: {
    transform: ({ src }) => {
      return {
        text: formatNumberToMoneyWithCurrencySymbol(src.freeCreditSales),
        value: src.freeCreditSales,
      };
    },
  },
  cashSales: {
    transform: ({ src }) => {
      return {
        text: formatNumberToMoneyWithCurrencySymbol(src.cashSales),
        value: src.cashSales,
      };
    },
  },
  creditDebitSales: {
    transform: ({ src }) => {
      return {
        text: formatNumberToMoneyWithCurrencySymbol(src.creditDebitSales),
        value: src.creditDebitSales,
      };
    },
  },
  otherSales: {
    transform: ({ src }) => {
      return {
        text: formatNumberToMoneyWithCurrencySymbol(src.otherSales),
        value: src.otherSales,
      };
    },
  },
};

export const graphTopUpTotal = {
  total: {
    key: "totalAmount",
  },
  barChartData: {
    transform: ({ src }) => {
      let topUps = [];
      const others = {
        value: 0,
        label: barSetting.OTHERS.label,
        color: barSetting.OTHERS.color,
      };
      src.topUpTotals?.forEach((total) => {
        const t = barSetting[total.paymentMethod];
        if (["CREDIT", "CASH", "CREDIT_DEBIT"].includes(total.paymentMethod)) {
          topUps.push({
            label: t.label,
            color: t.color,
            value: total.amount,
          });
        } else {
          others.value = others.value + Number(total.amount);
        }
      });
      topUps = others.value ? [...topUps, others] : topUps;
      return topUps;
    },
  },
  lineChartData: {
    transform: ({ src, params }) => {
      let plot = src.topUpPlots?.map((tu) => {
        const items = tu.paymentMethods
          ?.map((pm) => {
            return {
              name: ["CREDIT", "CASH", "CREDIT_DEBIT"].includes(pm.paymentMethod)
                ? barSetting[pm.paymentMethod]?.label
                : barSetting.OTHERS.label,
              value: pm.amount,
            };
          })
          .filter(({ value }) => {
            return Boolean(value);
          });
        if (tu) {
          return {
            timestamp: tu.timestamp,
            name: moment(tu.timestamp, getApiDateTimeFormatByTimeUnit(params.splitType)).format(
              getDateTimeFormatByTimeUnit(params.splitType)
            ),
            value: tu.totalAmount,
            items,
          };
        }
        return null;
      });
      if (params.interval === TimeUnit.Week || params.interval === TimeUnit.WeekS) {
        plot = calcWeekDataGraph(plot, params.interval === TimeUnit.WeekS);
      } else if (params.interval === TimeUnit.Quarter) {
        plot = calcQuarterDataGraph(plot);
      }
      return prepareChart(plot);
    },
  },
};

export const graphLineChartOfTotalCheckIns = {
  data: {
    transform: ({ src, params }) => {
      const plot = denormalizePlotDateTimeFromApiResponse(
        src.plot.map((p) => {
          const key = Object.keys(p)[0];
          return {
            [key]: p[key].newGuest,
          };
        }),
        params.interval
      );

      return prepareChart(plot);
    },
  },
  total: {
    transform: ({ src }) => {
      return {
        text: formatNumberWithComma(src.totalGuest),
        value: src.totalGuest,
      };
    },
  },
};

export const graphLineChartOfTotalReturns = {
  data: {
    transform: ({ src, params }) => {
      const plot = denormalizePlotDateTimeFromApiResponse(src.plot, params.interval);
      return prepareChart(plot);
    },
  },
  total: {
    transform: ({ src }) => {
      return {
        text: formatNumberToMoneyWithCurrencySymbol(src.totalSales),
        value: src.totalSales,
      };
    },
  },
};

export const viewTransactions = {
  _keys: [
    "location",
    "type",
    "voidReason",
    "note",
    "nfcStatus",
    "externalPaymentStatus",
    "externalPaymentVoidStatus",
  ],
  transactionDate: {
    transform: ({ src }) => {
      return timeAgo(src.deviceUpdatedDate);
    },
  },
  isVoided: {
    transform: ({ src }) => {
      if (src.externalPaymentVoidStatus) {
        return true;
      }
      return Boolean(
        src.transactionStatus === TransactionStatus.VOID || src.voidAuthorizationProfileId
      );
    },
  },
  dynamicPaymentMethod: {
    key: "dynamicPaymentMethod",
  },
  paymentMethod: {
    key: "paymentMethod",
  },
  isCheckout: {
    key: "isCheckout",
  },
  isFailed: {
    transform: ({ src }) => {
      return isFailedTransaction(
        src.externalPaymentVoidStatus ? src.externalPaymentVoidStatus : src.externalPaymentStatus,
        src.nfcStatus
      );
    },
  },
  registerInfo: {
    transform: ({ src }) => {
      const { deviceName, deviceSerialNumber } = src.device || {};
      return {
        shiftId: src.shiftId,
        deviceImei: src.deviceImei,
        deviceMacAddress: src.deviceMacAddress,
        deviceSerialNumber: deviceSerialNumber,
        deviceName,
      };
    },
  },
  guest: {
    transform: ({ src }) => {
      const { guest = {}, userTagUseCount = 0, userTagUid } = src;
      const { firstName, lastName, email, dob, phone, phoneCountryCode } = guest || {};
      const guestName = formatName(firstName, lastName);
      const name =
        guestName ||
        (userTagUseCount
          ? lang.populate(lang.nthGuestWhoUseThisTag, [formatNumberToOrdinal(userTagUseCount)])
          : "Unavailable");

      let postpaidCreditLimit = null;
      guest?.guestCheckins?.forEach((guestCheckin) => {
        guestCheckin.guestTags?.forEach((guestTag) => {
          postpaidCreditLimit = guestTag.postpaidCreditLimit;
        });
      });

      return {
        name,
        postpaidCreditLimit,
        tagUid: userTagUid,
        hasName: Boolean(guestName),
        email,
        phone: `${phoneCountryCode ? `+${phoneCountryCode.replace("+", "")}` : ""} ${phone || ""
          }`.trim(),
        dob: dob ? moment(dob).format(DateTime.A) : "",
      };
    },
  },
  previousBalance: {
    transform: ({ src }) => {
      let previousBalance = 0;
      let credits = src.userTagPreviousBalance;
      let freeCredits = src.userTagPreviousFreeCredits;

      previousBalance = credits;

      let postPaidCreditsUsed = 0;
      if (credits < 0) {
        postPaidCreditsUsed = Math.abs(credits);
      }

      return [
        [
          lang.previousBalance,
          formatNumberToMoneyWithCurrencySymbol(previousBalance),
          { strongTitle: false },
          "mt-md",
          Boolean(src.nfcStatus === "FAILED")
            ? "mt-md text-gray-300"
            : previousBalance < 0
              ? "mt-md text-red"
              : "mt-md text-black",
        ],
        [
          lang.credits,
          formatNumberToMoneyWithCurrencySymbol(credits < 0 ? 0 : credits),
          {},
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
        [
          lang.freeCredits,
          formatNumberToMoneyWithCurrencySymbol(freeCredits),
          {},
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
        [
          lang.postPaidCreditsUsed,
          formatNumberToMoneyWithCurrencySymbol(postPaidCreditsUsed),
          {},
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
      ];
    },
  },
  newBalance: {
    transform: ({ src }) => {
      let previousBalance = 0;
      let credits = src.userTagNewBalance;
      let freeCredits = src.userTagNewFreeCredits;

      if (credits >= 0) {
        previousBalance = credits + freeCredits;
      } else {
        previousBalance = credits;
      }

      let postPaidCreditsUsed = 0;
      if (credits < 0) {
        postPaidCreditsUsed = Math.abs(credits);
      }

      return [
        [
          lang.guestsNewBalance,
          formatNumberToMoneyWithCurrencySymbol(previousBalance),
          { strongTitle: true },
          "mt-md font-semibold",
          Boolean(src.nfcStatus === "FAILED")
            ? "mt-md text-md text-gray-300"
            : previousBalance < 0
              ? "mt-md text-md text-red"
              : "mt-md text-md text-black",
        ],
        [
          lang.credits,
          formatNumberToMoneyWithCurrencySymbol(credits < 0 ? 0 : credits),
          {},
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
        [
          lang.freeCredits,
          formatNumberToMoneyWithCurrencySymbol(freeCredits),
          {},
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
        [
          lang.postPaidCreditsUsed,
          formatNumberToMoneyWithCurrencySymbol(postPaidCreditsUsed),
          { superscript: src.type === TransactionType.ISSUE_FOC ? "1" : null },
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
      ];
    },
  },
  nfcStatus: {
    transform: ({ src }) => {
      return src.nfcStatus;
    },
  },
  totalPaid: {
    transform: ({ src }) => {
      const isReturn = src.type === TransactionType.RETURN;
      const paymentMethod = src.paymentMethod;
      let label = isReturn
        ? lang.totalReturnedCredits
        : src.nfcStatus === "FAILED" && src.transactionStatus !== "VOID"
          ? lang.total
          : lang.totalPaid;
      if (src.type === TransactionType.ISSUE_FOC) {
        label = lang.totalFreeCreditsIssued;
      } else if (src.type === TransactionType.REMOVE_FOC) {
        label = lang.totalFreeCreditsRemoved;
      }

      let items = [
        [
          label,
          formatNumberToMoneyWithCurrencySymbol(src.paidAmount),
          {
            fontSize: "text-lg",
            danger: isReturn,
            strong: true,
            lineThrough: src.externalPaymentVoidStatus
              ? src.externalPaymentVoidStatus === ExternalPaymentVoidStatus.Success
              : Boolean(
                src.transactionStatus === "VOID" &&
                (src.voidAuthorizationProfileId || src.voidDeviceDate)
              ),
          },
          "mt-md font-semibold text-md",
          Boolean(src.nfcStatus === "FAILED")
            ? "mt-md text-gray-300 font-semibold"
            : "mt-md text-black font-semibold",
        ],
      ];

      let remainingAmount = src.paidAmount;
      let paidCredits = 0;
      let paidFreeCredits = 0;
      let paidPostpaidCredits = 0;
      const totalAdditionalCharge = src.transactionAdditionalCharges?.reduce(
        (acc, curr) => (acc += curr.chargedAmount),
        0
      );

      if (remainingAmount > 0 && src.userTagPreviousFreeCredits > 0) {
        if (src.userTagPreviousFreeCredits > remainingAmount) {
          paidFreeCredits = remainingAmount - (totalAdditionalCharge + src.tipAmount);
        } else {
          paidFreeCredits = src.userTagPreviousFreeCredits;
        }
        remainingAmount = remainingAmount - paidFreeCredits;
      }

      if (remainingAmount > 0) {
        if (src.userTagPreviousBalance <= 0) {
          paidCredits = 0;
          paidPostpaidCredits = remainingAmount;
        } else {
          if (src.userTagPreviousBalance > remainingAmount) {
            paidCredits = remainingAmount;
            paidPostpaidCredits = 0;
          } else {
            paidCredits = src.userTagPreviousBalance;
            remainingAmount = remainingAmount - paidCredits;
            paidPostpaidCredits = remainingAmount;
          }
        }
      }

      const isPaidByCashOrCard = [
        PaymentMethod.Cash,
        PaymentMethod.CreditDebit,
        PaymentMethod.Credit,
        PaymentMethod.EWallet,
        PaymentMethod.Others,
      ].includes(paymentMethod);

      if (
        (src.type === TransactionType.SALE || src.type === TransactionType.MIXED) &&
        !isPaidByCashOrCard
      ) {
        items.push([
          lang.credits,
          formatNumberToMoneyWithCurrencySymbol(paidCredits),
          {},
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ]);

        items.push([
          lang.freeCredits,
          formatNumberToMoneyWithCurrencySymbol(paidFreeCredits),
          {},
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ]);

        items.push([
          lang.postPaidCreditsUsed,
          formatNumberToMoneyWithCurrencySymbol(paidPostpaidCredits),
          {},
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ]);
      }

      if (src.type === TransactionType.SALE && paymentMethod === PaymentMethod.Cash) {
        let totalCashPaid = src.paidAmount + src.cashChange;
        items.push([
          lang.cash,
          formatNumberToMoneyWithCurrencySymbol(totalCashPaid),
          "text-black",
          "text-black",
        ]);

        items.push([
          lang.change,
          formatNumberToMoneyWithCurrencySymbol(src.cashChange),
          "text-black",
          "text-black",
        ]);
      }

      if (
        src.type === TransactionType.SALE &&
        (paymentMethod === PaymentMethod.CreditDebit ||
          paymentMethod === PaymentMethod.Credit ||
          paymentMethod === PaymentMethod.Others)
      ) {
        items.push([
          prettifyPaymentMethod(src.paymentMethod, src.dynamicPaymentMethod),
          !src.externalPaymentStatus || src.externalPaymentStatus === ExternalPaymentStatus.Success
            ? formatNumberToMoneyWithCurrencySymbol(src.paidAmount)
            : "-",
          "text-black",
          "text-black",
        ]);
        if (src.externalPaymentStatus) {
          items.push([
            lang.cardNumber,
            src.cardNumber ? src.cardNumber : "-",
            "text-black",
            "text-black",
          ]);
          items.push([
            lang.invoiceNumber,
            src.ghlInvoiceNumber ? src.ghlInvoiceNumber : "-",
            "text-black",
            "text-black",
          ]);
          items.push([
            lang.cardApprovalNumber,
            src.cardApprovalNumber ? src.cardApprovalNumber : "-",
            "text-black",
            "text-black",
          ]);
        } else {
          items.push([
            lang.referenceNo,
            src.referenceNumber ? src.referenceNumber : "-",
            "text-black",
            "text-black",
          ]);
        }
      }
      if (
        src.type === TransactionType.TOPUP &&
        (paymentMethod === PaymentMethod.CreditDebit || paymentMethod === PaymentMethod.Credit)
      ) {
        if (src.externalPaymentStatus) {
          items.push([
            prettifyPaymentMethod(src.paymentMethod, src.dynamicPaymentMethod),
            src.externalPaymentStatus === ExternalPaymentStatus.Success
              ? formatNumberToMoneyWithCurrencySymbol(src.paidAmount)
              : "-",
            "text-black",
            "text-black",
          ]);
          items.push([
            lang.invoiceNumber,
            src.ghlInvoiceNumber ? src.ghlInvoiceNumber : "-",
            "text-black",
            "text-black",
          ]);
          items.push([
            lang.cardApprovalNumber,
            src.cardApprovalNumber ? src.cardApprovalNumber : "-",
            "text-black",
            "text-black",
          ]);
        }
      }
      if (
        (src.type === TransactionType.SALE || src.type === TransactionType.TOPUP) &&
        paymentMethod === PaymentMethod.EWallet
      ) {
        items.push([
          src.tradeType ? src.tradeType : lang.eWallet,
          src.externalPaymentStatus === ExternalPaymentStatus.Success
            ? formatNumberToMoneyWithCurrencySymbol(src.paidAmount)
            : "-",
          "text-black",
          "text-black",
        ]);
        items.push([
          lang.populate(lang.tradeTypeTransactionNumber, [
            src.tradeType ? src.tradeType : lang.eWallet,
          ]),
          src.tradeTransactionNumber ? src.tradeTransactionNumber : "-",
          "text-black",
          "text-black",
        ]);
        items.push([
          lang.invoiceNumber,
          src.ghlInvoiceNumber ? src.ghlInvoiceNumber : "-",
          "text-black",
          "text-black",
        ]);
      }

      return items;
    },
  },
  voidTotalPaid: {
    transform: ({ src }) => {
      const isReturn = src.type === TransactionType.RETURN;
      const paymentMethod = src.paymentMethod;
      const isPaidByCashOrCard = [
        PaymentMethod.Cash,
        PaymentMethod.CreditDebit,
        PaymentMethod.Credit,
        PaymentMethod.EWallet,
        PaymentMethod.Others,
      ].includes(paymentMethod);
      let label = isReturn
        ? lang.totalReturnedCredits
        : src.nfcStatus === "FAILED"
          ? lang.total
          : lang.totalReturnedCredits;
      if (src.type === TransactionType.ISSUE_FOC) {
        label = lang.totalFreeCreditsIssued;
      } else if (src.type === TransactionType.REMOVE_FOC) {
        label = lang.totalFreeCreditsRemoved;
      } else if (src.type === TransactionType.SALE && isPaidByCashOrCard) {
        label = lang.totalRefunded;
      }

      let items = [
        [
          label,
          formatNumberToMoneyWithCurrencySymbol(src.paidAmount),
          {
            fontSize: "text-lg",
            danger: isReturn,
            strong: true,
            lineThrough: Boolean(src.voidAuthorizationProfileId),
          },
          "mt-md font-semibold text-md",
          Boolean(src.nfcStatus === "FAILED")
            ? "mt-md text-gray-300 font-semibold"
            : "mt-md text-black font-semibold",
        ],
      ];

      if (src.type === TransactionType.SALE && paymentMethod === PaymentMethod.Cash) {
        let totalCashPaid =
          src.transactionStatus === "VOID" ? src.paidAmount : src.paidAmount + src.cashChange;
        items.push([
          lang.cash,
          formatNumberToMoneyWithCurrencySymbol(totalCashPaid),
          "text-black",
          "text-black",
        ]);
      }

      if (
        src.type === TransactionType.SALE &&
        (paymentMethod === PaymentMethod.CreditDebit ||
          paymentMethod === PaymentMethod.Credit ||
          paymentMethod === PaymentMethod.Others)
      ) {
        items.push([
          paymentMethod === PaymentMethod.Others ? lang.others : lang.creditDebit,
          formatNumberToMoneyWithCurrencySymbol(src.paidAmount),
          "text-black",
          "text-black",
        ]);
        items.push([
          lang.referenceNo,
          src.voidReferenceNumber ? src.voidReferenceNumber : "-",
          "text-black",
          "text-black",
        ]);
      }

      return items;
    },
  },
  voidedNewBalance: {
    transform: ({ src }) => {
      let postPaidCreditsUsed = 0;
      if (src.voidUserTagNewBalance < 0) {
        postPaidCreditsUsed = Math.abs(src.voidUserTagNewBalance);
      }

      let credits = src.voidUserTagNewBalance;

      return [
        [
          lang.guestsNewBalance,
          formatNumberToMoneyWithCurrencySymbol(credits),
          "mt-md text-black-600 font-semibold",
          Boolean(src.nfcStatus === "FAILED")
            ? "mt-md text-gray-300 text-md font-semibold"
            : credits < 0
              ? "mt-md text-red text-md font-semibold"
              : "mt-md text-black text-md font-semibold",
        ],
        [
          lang.credits,
          formatNumberToMoneyWithCurrencySymbol(
            src.voidUserTagNewBalance > 0 ? src.voidUserTagNewBalance : 0
          ),
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
        [
          lang.freeCredits,
          formatNumberToMoneyWithCurrencySymbol(
            src.voidUserTagNewFreeCredits < 0 ? 0 : src.voidUserTagNewFreeCredits
          ),
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
        [
          lang.postPaidCreditsUsed,
          formatNumberToMoneyWithCurrencySymbol(postPaidCreditsUsed),
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
      ];
    },
  },
  voidedPreviousBalance: {
    transform: ({ src }) => {
      let postPaidCreditsUsed = 0;
      if (src.voidUserTagPreviousBalance < 0) {
        postPaidCreditsUsed = Math.abs(src.voidUserTagPreviousBalance);
      }

      let credits = src.voidUserTagPreviousBalance;

      return [
        [
          lang.previousBalance,
          formatNumberToMoneyWithCurrencySymbol(credits),
          "mt-md text-black-600",
          Boolean(src.nfcStatus === "FAILED")
            ? "mt-md text-gray-300 font-semibold"
            : credits < 0
              ? "mt-md text-red font-semibold"
              : "mt-md text-black font-semibold",
        ],
        [
          lang.credits,
          formatNumberToMoneyWithCurrencySymbol(
            src.voidUserTagPreviousBalance > 0 ? src.voidUserTagPreviousBalance : 0
          ),
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
        [
          lang.freeCredits,
          formatNumberToMoneyWithCurrencySymbol(src.voidUserTagPreviousFreeCredits),
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
        [
          lang.postPaidCreditsUsed,
          formatNumberToMoneyWithCurrencySymbol(postPaidCreditsUsed),
          "text-gray-400",
          Boolean(src.nfcStatus === "FAILED") ? "text-gray-300" : "text-gray-400",
        ],
      ];
    },
  },
  hasItems: {
    transform: ({ src }) => {
      return Boolean(src.transactionItems?.length);
    },
  },
  items: {
    transform: ({ src }) => {
      let row = [];
      const taxes = {};
      const tip = {};
      let totalItems = 0;
      let totalDeductions = {};
      let totalTaxAmount = 0;
      let quantity = 0;
      let amount = 0;
      const cartDiscount = {};
      const itemDiscounts = [];
      const type = src.type;

      if ([TransactionType.TOPUP, TransactionType.RETURN].includes(src.type)) {
        const isReturn = src.type === TransactionType.RETURN;
        row.push({
          type: "item",
          name: isReturn ? lang.credits : lang.topupCredits,
          info: isReturn ? "" : prettifyPaymentMethod(src.paymentMethod, src.dynamicPaymentMethod),
          amount: formatNumberToMoneyWithCurrencySymbol(src.paidAmount),
        });
      }
      if (src.type === TransactionType.ISSUE_FOC) {
        row.push({
          type: "item",
          name: lang.freeCredits,
          amount: formatNumberToMoneyWithCurrencySymbol(
            src.userTagNewFreeCredits - src.userTagPreviousFreeCredits
          ),
          size: "text-lg",
        });
      } else if (src.type === TransactionType.REMOVE_FOC) {
        row.push({
          type: "item",
          name: lang.freeCredits,
          amount: formatNumberToMoneyWithCurrencySymbol(
            src.userTagPreviousFreeCredits - src.userTagNewFreeCredits
          ),
          size: "text-lg",
        });
      }

      if (src.tipAmount) {
        tip.amount = src.tipAmount;
        if (src.tipId) {
          tip.percentage = src.tip.value;
        }
      }

      let summaryPrices = [
        {
          value: formatNumberToMoneyWithCurrencySymbol(src.originalAmount),
        },
      ];

      let calculatedSubTotal = 0;
      src.transactionItems.forEach((transaction) => {
        let totalPrice = src.isSiigoTransaction ? add(transaction.totalAmount, transaction.additionalChargeAmount) : transaction.totalAmount;
        const deductions = [];
        if (transaction.transactionVoucherItem) {
          const voucher = transaction.transactionVoucherItem;
          voucher.name = `${voucher.quantity} x ${voucher.itemName}`;
          deductions.push({ label: "Voucher", value: voucher.name });
          const voucherAmount = multiply(transaction.retailPrice, voucher.quantity);
          totalDeductions.voucher =
            totalDeductions && totalDeductions.voucher
              ? add(totalDeductions.voucher, voucherAmount)
              : voucherAmount;
          // re calculate total price in case of non siigo transaction 
          if (!src.isSiigoTransaction) {
            totalPrice = subtract(totalPrice, voucherAmount);
          }
        }

        if (transaction.discountAmount) {
          const discount = {
            discountName: transaction.discountName || "-",
            discountId: transaction.discountId,
            discountIdiscountAmountd: transaction.discountId,
            discountAmount: divide(transaction.discountAmount, transaction.discountQuantity),
            totalDiscountAmount: transaction.discountAmount,
            discountPercentage: transaction.discountPercentage,
            discountType: transaction.discountType,
            discountQuantity: transaction.discountQuantity,
            discountAmountRaw: transaction.discountAmount,
          };
          if (transaction.transactionDiscountType === "CART_DISCOUNT") {
            cartDiscount[discount.discountId] = {
              ...discount,
              totalQuantity: cartDiscount[discount.discountId]
                ? add(cartDiscount[discount.discountId].discountQuantity, discount.discountQuantity)
                : discount.discountQuantity,
            };
          } else if (transaction.transactionDiscountType === "ITEM_DISCOUNT") {
            let existingDiscount = itemDiscounts.find(
              (d) =>
                d.discountId === discount.discountId && d.discountName === discount.discountName
            );
            if (existingDiscount) {
              if (existingDiscount.discountAmountRaw < discount.discountAmountRaw) {
                existingDiscount.discountAmountRaw = discount.discountAmountRaw;
              }
              if (existingDiscount.discountPercentage < discount.discountPercentage) {
                existingDiscount.discountPercentage = discount.discountPercentage;
              }
              existingDiscount.totalDiscountAmount = add(
                existingDiscount.totalDiscountAmount,
                discount.totalDiscountAmount
              );
            } else {
              itemDiscounts.push(discount);
            }
          } else {
            deductions.push({
              label: lang.discount,
              value: `${discount.discountName} ${discount.discountType === "PERCENTAGE"
                ? `(${discount.discountPercentage}%)`
                : `(${formatNumberToMoneyWithCurrencySymbol(discount.discountAmount)} off)`
                }`,
              text: `( ${discount.discountQuantity} x ${formatNumberToMoneyWithCurrencySymbol(
                discount.discountAmount
              )} )`,
            });
          }
          totalPrice = subtract(totalPrice, discount.totalDiscountAmount);
        }

        const itemRedeem = transaction.transactionVoucherItem
          ? transaction.quantity === transaction.transactionVoucherItem.quantity
          : false;
        if (transaction.taxId && type !== TransactionType.Redeemed && !itemRedeem) {
          const tax = {
            taxId: transaction.taxId,
            taxName: transaction.taxName,
            taxAmount: transaction.taxAmount,
            taxPercentage: transaction.taxPercentage,
          };
          deductions.push({
            label: lang.tax,
            value: `${tax.taxName} (${tax.taxPercentage}%)`,
            text: formatNumberToMoneyWithCurrencySymbol(tax.taxAmount),
          });
          totalTaxAmount = (Number(totalTaxAmount) + Number(tax.taxAmount)).toFixed(2);
          taxes[tax.taxId] = taxes[tax.taxId]
            ? {
              ...taxes[tax.taxId],
              amount: tax.taxAmount + taxes[tax.taxId].amount,
            }
            : {
              rate: tax.taxPercentage,
              amount: tax.taxAmount,
              name: tax.taxName,
            };
        }

        totalItems = add(totalItems, transaction.quantity);
        quantity = quantity + transaction.quantity;
        amount = amount + transaction.retailPrice;
        if (src.isSiigoTransaction) {
          calculatedSubTotal = add(calculatedSubTotal, totalPrice);
        }
        row.push({
          type: "productItem",
          product: {
            productId: transaction.productId,
            productName: transaction.productName || "",
            productVariants: transaction.productVariants,
            info: transaction.productSkuDeleted ? `(${lang.deleted})` : "",
            isDeleted: transaction.productSkuDeleted,
          },
          quantity: `${transaction.quantity} x ${formatNumberToMoneyWithCurrencySymbol(
            transaction.bargainPrice != null ? transaction.bargainPrice : transaction.retailPrice
          )}`,

          total: formatNumberToMoneyWithCurrencySymbol(totalPrice < 0 ? 0 : totalPrice),
          originalPrice:
            totalPrice !== transaction.totalAmount
              ? formatNumberToMoneyWithCurrencySymbol(transaction.totalAmount)
              : null,
          originalBargainPrice:
            transaction.bargainPrice != null
              ? formatNumberToMoneyWithCurrencySymbol(transaction.retailPrice)
              : null,
          bargainPrice: formatNumberToMoneyWithCurrencySymbol(transaction.bargainPrice),
          bargain: transaction.bargainPrice != null,
          deductions,
          hasDiscounts: transaction.discountAmount > 0,
          discounts: {
            text: transaction.discountQuantity ? lang.discount : "",
            prices: transaction.discountQuantity
              ? `${transaction.discountType === "PERCENTAGE"
                ? `${transaction.discountPercentage}%`
                : `${formatNumberToMoneyWithCurrencySymbol(
                  divide(transaction.discountAmount, transaction.discountQuantity)
                )}`
              }`
              : "",
            quantity: transaction.discountQuantity ? transaction.discountQuantity : "",
            discountName: transaction.discountName,
            percentage: transaction.discountQuantity
              ? `${transaction.discountType === "PERCENTAGE"
                ? `(${transaction.discountPercentage}%)`
                : `(${formatNumberToMoneyWithCurrencySymbol(
                  divide(transaction.discountAmount, transaction.discountQuantity)
                )})`
              }`
              : "",
            quantityTimesPrice: transaction.discountQuantity
              ? `(${transaction.discountQuantity} x ${transaction.discountType === "PERCENTAGE"
                ? `${transaction.discountPercentage}%`
                : `${formatNumberToMoneyWithCurrencySymbol(
                  divide(transaction.discountAmount, transaction.discountQuantity)
                )}`
              })`
              : "",
            discountAmountRaw: transaction.discountQuantity
              ? `${transaction.discountType === "PERCENTAGE"
                ? `(${transaction.discountPercentage}%)`
                : `(${formatNumberToMoneyWithCurrencySymbol(transaction.discountAmount)})`
              }`
              : "",
          },
          note: transaction.note,
        });
      });


      if (src.isSiigoTransaction) {
        
        summaryPrices = [
          {
            value: formatNumberToMoneyWithCurrencySymbol(calculatedSubTotal),
          },
        ];
      }


      if (src.paidAmount !== src.originalAmount) {
        summaryPrices.push({
          value: formatNumberToMoneyWithCurrencySymbol(src.paidAmount),
          deduction: true,
          lineThrough: src.paidAmount !== src.originalAmount,
        });
      }

      row.push({
        type: "summary",
        text: lang.subTotal,
        quantity: ` ${quantity} item${quantity > 1 ? "s" : ""}`,
        prices: summaryPrices,
        classNames: "font-semibold",
      });

      if (src.cartDiscount) {
        const d = src.cartDiscount;
        row.push({
          type: "summary",
          text: `${lang.discount} ${d.discountType === "PERCENTAGE"
            ? `(${d.discountPercentage}%)`
            : `(${formatNumberToMoneyWithCurrencySymbol(d.discountAmount)} off)`
            }`,
          description:
            `${d.discountName} (${formatNumberToMoneyWithCurrencySymbol(d.discountAmount)})` || "-",
          prices: [
            {
              value: `- ${formatNumberToMoneyWithCurrencySymbol(d.discountAmount)}`,
              discount: true,
            },
          ],
        });
      }

      itemDiscounts.forEach((d) => {
        row.push({
          type: "summary",
          text: `${lang.discount} ${d.discountType === "PERCENTAGE"
            ? `(${d.discountPercentage}%)`
            : `(${formatNumberToMoneyWithCurrencySymbol(d.discountAmountRaw)} off)`
            }`,
          description: `${d.discountName}` || "-",
          prices: [
            {
              value: `(${formatNumberToMoneyWithCurrencySymbol(d.totalDiscountAmount)})`,
            },
          ],
        });
      });

      if (src.transactionAdditionalCharges && src.transactionAdditionalCharges.length > 0) {
        for (let addCharge of src.transactionAdditionalCharges) {
          row.push({
            type: "additionalCharge",
            text: `${addCharge.name} (${addCharge.type === "FIXED"
              ? `${formatNumberToMoneyWithCurrencySymbol(addCharge.value)}`
              : `${addCharge.value}%`
              })`,
            amount: `${formatNumberToMoneyWithCurrencySymbol(addCharge.chargedAmount)}`,
            classNames: "font-semibold",
          });
        }
      }

      row.push({
        type: "summary",
        text: lang.total,
        prices: [
          {
            value: formatNumberToMoneyWithCurrencySymbol(src.paidAmount),
          },
        ],
        classNames: "font-semibold",
      });

      if (Object.keys(taxes)?.length) {
        const totalTaxCount = Object.keys(taxes).length;
        row.push({
          type: "tax",
          text: lang.tax,
          description: `${totalTaxCount} Tax${totalTaxCount > 1 ? "es" : ""}`,
          value: formatNumberToMoneyWithCurrencySymbol(totalTaxAmount),
          taxes: Object.keys(taxes).map((tax) => {
            const t = taxes[tax];
            return {
              name: "",
              taxName: `${t.name} (${t.rate}%)`,
              rate: formatNumberToMoneyWithCurrencySymbol(t.amount),
            };
          }),
          classNames: "font-semibold",
        });
      }

      if (Object.keys(tip)?.length) {
        row.push({
          type: "tip",
          text: lang.tip,
          value: formatNumberToMoneyWithCurrencySymbol(tip.amount),
          percentage: tip.percentage ? ` (${tip.percentage}%)` : "",
          classNames: "font-semibold",
        });
      }

      return row;
    },
  },
  historyTable: {
    transform: ({ src }) => {
      const {
        voidAuthorizationProfileId,
        voidAuthorizationTagUid,
        type,
        paymentMethod,
        externalPaymentStatus,
        externalPaymentVoidStatus,
      } = src || {};
      const isVoided = Boolean(
        src.transactionStatus === "VOID" && (voidAuthorizationProfileId || voidAuthorizationTagUid)
      );
      const idFailed = isFailedTransaction(externalPaymentStatus, src.nfcStatus);
      const data = [];
      const isPaidByCashOrCard = [
        PaymentMethod.Cash,
        PaymentMethod.CreditDebit,
        PaymentMethod.Credit,
        PaymentMethod.Others,
        PaymentMethod.EWallet,
      ].includes(paymentMethod);
      if (isVoided) {
        const {
          voidUserTagTapCount = 0,
          voidDeviceDate = null,
          voidStaffTagUid,
          voidAuthorizationTagUid = null,
          voidStaff = {},
          voidAuthorization = {},
        } = src;
        const { firstName, lastName } = voidStaff || {};
        const { firstName: authFirstName, lastName: authLastName } = voidAuthorization || {};
        const dateTime = timeAgo(voidDeviceDate, true);
        data.push({
          tapCount:
            isPaidByCashOrCard && src.type === TransactionType.SALE ? (
              <Text color="text-gray">{lang.none}</Text>
            ) : (
              formatNumberToOrdinal(voidUserTagTapCount)
            ),
          dateTime: {
            date: dateTime.date || "-",
            time: dateTime.time || "-",
          },
          staff: {
            name: !isPaidByCashOrCard
              ? formatName(firstName, lastName)
              : formatName(authFirstName, authLastName) || "-",
            tagUid: !isPaidByCashOrCard ? voidStaffTagUid : voidAuthorizationTagUid,
          },
          authorization: {
            name: formatName(authFirstName, authLastName) || "-",
            tagUid: voidAuthorizationTagUid,
          },
          status:
            (isFailedTransaction(externalPaymentVoidStatus, src.nfcStatus) ? lang.failed : "") +
            " " +
            prettifyTransactionType(src.type) +
            " " +
            lang.voided,
        });
      }
      const { userTagTapCount = 0, deviceCreatedDate, staff = {}, staffTagUid } = src;
      const { firstName, lastName } = staff || {};
      const d = timeAgo(deviceCreatedDate, true);
      const dateTime = {
        date: d.date || "-",
        time: d.time || "-",
      };
      const staffName = {
        name: formatName(firstName, lastName) || "-",
        tagUid: staffTagUid,
      };
      const historySale = {
        tapCount:
          isPaidByCashOrCard && src.type === TransactionType.SALE ? (
            <Text color="text-gray">{lang.none}</Text>
          ) : (
            formatNumberToOrdinal(userTagTapCount)
          ),
        dateTime,
        staff: staffName,
      };
      if (type === TransactionType.MIXED) {
        data.push({
          ...historySale,
          status:
            (idFailed ? lang.failed : "") + " " + prettifyTransactionType(TransactionType.SALE),
        });
        data.push({
          ...historySale,
          status:
            (idFailed ? lang.failed : "") + " " + prettifyTransactionType(TransactionType.REDEEM),
        });
      } else {
        data.push({
          ...historySale,
          status: (idFailed ? lang.failed : "") + " " + prettifyTransactionType(type),
        });
      }

      return {
        data,
        columns: [
          {
            text: lang.tapCount,
            key: "tapCount",
          },
          {
            text: lang.date,
            key: "dateTime",
            custom: true,
          },
          {
            text: lang.staff,
            key: "staff",
            custom: true,
          },
          ...(isVoided
            ? [
              {
                text: lang.voidAuthorization,
                key: "authorization",
                custom: true,
              },
            ]
            : []),
          {
            text: lang.type,
            key: "status",
          },
        ],
      };
    },
  },
};

// GET /transaction/{id}/pos-sync/detail -- see ms-inventory-integration-reference.md §12/§14.
// `bill` is null until a bill has synced against this transaction; amounts/dates are
// pre-formatted here so the panel component stays purely presentational.
export const transactionPosSyncDetail = {
  bill: {
    transform: ({ src }) => {
      const bill = src?.bill;
      if (!bill) {
        return null;
      }
      const money = (v) => formatNumberToMoneyWithCurrencySymbol(v || 0);
      const dateOrDash = (v) => (v ? moment(v).format(DateTime.A) : "-");
      const itemLookup = buildItemLookup(bill.orders);

      return {
        billHeaderId: bill.billHeaderId,
        billLocalId: bill.billLocalId,
        billRefCode: bill.billRefCode,
        billType: bill.billType,
        status: bill.status,
        grossAmount: money(bill.grossAmount),
        discountAmount: money(bill.discountAmount),
        voucherAmount: money(bill.voucherAmount),
        taxAmount: money(bill.taxAmount),
        netAmount: money(bill.netAmount),
        paidAmount: money(bill.paidAmount),
        openedAt: dateOrDash(bill.openedAt),
        settledAt: dateOrDash(bill.settledAt),
        splits: (bill.splits || []).map((split) => ({
          billSplitId: split.billSplitId,
          billSplitLocalId: split.billSplitLocalId,
          splitNumber: split.splitNumber,
          splitType: split.splitType,
          status: split.status,
          guestRef: split.guestRef,
          splitAmount: money(split.splitAmount),
          paidAmount: money(split.paidAmount),
          discountAmount: money(split.discountAmount),
          voucherAmount: money(split.voucherAmount),
          reconciliationStatus: split.reconciliationStatus,
          items: mapSplitItems(split.itemsSnapshot, itemLookup),
          payments: (split.payments || []).map((payment) => ({
            transactionId: payment.transactionId,
            method: payment.method,
            amount: money(payment.amount),
            referenceId: payment.referenceId,
            status: payment.status,
            paidAt: dateOrDash(payment.paidAt),
            isSelected: Boolean(payment.isSelected),
          })),
        })),
        orders: (bill.orders || []).map((order) => ({
          orderId: order.orderId,
          orderLocalId: order.orderLocalId,
          status: order.status,
          orderNote: order.orderNote,
          isPrepaid: Boolean(order.isPrepaid),
          items: (order.items || []).map((item) => ({
            itemLocalId: item.itemLocalId,
            productName: item.productName || item.itemLocalId,
            quantity: item.quantity,
            price: money(item.price),
            taxAmount: money(item.taxAmount),
            serviceChargeAmount: money(item.serviceChargeAmount),
            notes: item.notes || "-",
            status: item.status,
          })),
        })),
        reservation: bill.reservation
          ? {
            posReservationId: bill.reservation.posReservationId,
            sessionId: bill.reservation.sessionId,
            status: bill.reservation.status,
            resourceId: bill.reservation.resourceId,
            guestCount: bill.reservation.guestCount,
            closeReason: bill.reservation.closeReason,
            guests: (bill.reservation.guests || []).map((guest) => ({
              name: guest.name || guest.guestRef || "-",
              guestType: guest.guestType,
              isPrimary: Boolean(guest.isPrimary),
            })),
          }
          : null,
      };
    },
  },
};
